import { useState, useEffect } from 'react';

const API = import.meta.env.VITE_API_URL || '/api';

export default function InventoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState({});
  const token = localStorage.getItem('admin_token');

  const fetchInventory = () => {
    setLoading(true);
    fetch(`${API}/get-products.php`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setItems(d.products || []))
      .catch(() => setItems([])).finally(() => setLoading(false));
  };

  useEffect(() => { fetchInventory(); }, []);

  const updateStock = async (id, newStock) => {
    setSaving(s => ({ ...s, [id]: true }));
    await fetch(`${API}/update-stock.php`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ id, stock: newStock }),
    });
    setSaving(s => ({ ...s, [id]: false }));
    setItems(items.map(it => it.id === id ? { ...it, stock: newStock } : it));
  };

  const filtered = items.filter(p => !search || p.name?.toLowerCase().includes(search.toLowerCase()));

  const stockStatus = (s) => s === 0 ? { label: 'Out of Stock', color: '#C0392B' } : s <= 5 ? { label: 'Critical', color: '#FF6B00' } : s <= 15 ? { label: 'Low', color: '#FFB800' } : { label: 'In Stock', color: '#4CAF50' };

  return (
    <div>
      <h1 style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: '1.5rem', background: 'linear-gradient(135deg,#FFE082,#FF6B00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '1.8rem' }}>🗃️ Inventory Management</h1>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total Products', val: items.length, color: '#FFB800' },
          { label: 'In Stock', val: items.filter(p => p.stock > 15).length, color: '#4CAF50' },
          { label: 'Low Stock', val: items.filter(p => p.stock > 0 && p.stock <= 15).length, color: '#FFB800' },
          { label: 'Out of Stock', val: items.filter(p => p.stock === 0).length, color: '#C0392B' },
        ].map(c => (
          <div key={c.label} style={{ background: 'linear-gradient(135deg,rgba(255,107,0,.06),rgba(36,0,70,.25))', border: '1px solid rgba(255,184,0,.15)', borderRadius: 8, padding: '1.2rem', textAlign: 'center' }}>
            <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: '1.6rem', color: c.color }}>{c.val}</div>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: '.62rem', letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(255,224,130,.5)', marginTop: '.3rem' }}>{c.label}</div>
          </div>
        ))}
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products…"
        style={{ width: '100%', maxWidth: 400, padding: '.7rem 1rem', marginBottom: '1.2rem', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,184,0,.22)', borderRadius: 6, color: '#FFE082', fontFamily: "'Crimson Pro',serif", fontSize: '.9rem', outline: 'none', boxSizing: 'border-box' }} />

      <div style={{ background: 'linear-gradient(135deg,rgba(20,4,0,.95),rgba(26,10,46,.95))', border: '1px solid rgba(255,184,0,.18)', borderRadius: 10, overflow: 'auto' }}>
        {loading ? <div style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,224,130,.4)', fontFamily: "'Cinzel',serif" }}>🌀 Loading inventory…</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,184,0,.1)' }}>
                {['Product', 'Category', 'Price', 'Current Stock', 'Status', 'Update Stock'].map(h => (
                  <th key={h} style={{ padding: '.7rem 1rem', textAlign: 'left', fontFamily: "'Cinzel',serif", fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(255,184,0,.5)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const ss = stockStatus(p.stock || 0);
                return (
                  <tr key={p.id} style={{ borderBottom: '1px solid rgba(255,184,0,.06)' }}>
                    <td style={{ padding: '.75rem 1rem' }}>
                      <div style={{ fontFamily: "'Cinzel',serif", fontSize: '.78rem', color: '#FFE082' }}>{p.name}</div>
                    </td>
                    <td style={{ padding: '.75rem 1rem', fontFamily: "'Cinzel',serif", fontSize: '.68rem', color: '#FF6B00', textTransform: 'uppercase', letterSpacing: '.1em' }}>{p.category_id}</td>
                    <td style={{ padding: '.75rem 1rem', fontFamily: "'Cinzel',serif", fontSize: '.78rem', color: '#FFB800', fontWeight: 700 }}>₹{Number(p.price).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '.75rem 1rem', fontFamily: "'Cinzel Decorative',serif", fontSize: '1rem', color: ss.color, fontWeight: 700 }}>{p.stock || 0}</td>
                    <td style={{ padding: '.75rem 1rem' }}>
                      <span style={{ background: `${ss.color}18`, color: ss.color, border: `1px solid ${ss.color}44`, borderRadius: 20, padding: '.2rem .7rem', fontFamily: "'Cinzel',serif", fontSize: '.6rem', letterSpacing: '.1em', textTransform: 'uppercase' }}>{ss.label}</span>
                    </td>
                    <td style={{ padding: '.75rem 1rem' }}>
                      <div style={{ display: 'flex', gap: '.4rem', alignItems: 'center' }}>
                        <button onClick={() => updateStock(p.id, Math.max(0, (p.stock || 0) - 1))} disabled={saving[p.id]}
                          style={{ width: 28, height: 28, background: 'rgba(255,107,0,.15)', border: '1px solid rgba(255,107,0,.3)', borderRadius: 4, color: '#FF6B00', cursor: 'pointer', fontWeight: 700 }}>−</button>
                        <input type="number" value={p.stock || 0} min="0"
                          onChange={e => setItems(items.map(it => it.id === p.id ? { ...it, stock: parseInt(e.target.value) || 0 } : it))}
                          onBlur={e => updateStock(p.id, parseInt(e.target.value) || 0)}
                          style={{ width: 60, padding: '.3rem .4rem', textAlign: 'center', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,184,0,.22)', borderRadius: 4, color: '#FFE082', fontFamily: "'Cinzel',serif", fontSize: '.78rem', outline: 'none' }} />
                        <button onClick={() => updateStock(p.id, (p.stock || 0) + 1)} disabled={saving[p.id]}
                          style={{ width: 28, height: 28, background: 'rgba(76,175,80,.15)', border: '1px solid rgba(76,175,80,.3)', borderRadius: 4, color: '#4CAF50', cursor: 'pointer', fontWeight: 700 }}>+</button>
                        {saving[p.id] && <span style={{ fontFamily: "'Cinzel',serif", fontSize: '.6rem', color: '#FFB800' }}>…</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
